import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { getVerse } from '../services/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { BookOpen, ArrowRight, Loader2, Sparkles, ChevronLeft, Network } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REF_RE = /^\s*((?:[1-3]\s)?[A-Za-z][A-Za-z\s]+?)\s+(\d+):(\d+)(?:-\d+)?\s*$/;
const parseRef = (raw) => {
  const m = String(raw || '').match(REF_RE);
  if (!m) return null;
  return { book: m[1].trim(), chapter: parseInt(m[2], 10), verse: parseInt(m[3], 10) };
};

/**
 * PublicVerseReader — a free, read-only Scripture preview for visitors who
 * aren't logged in. Opens the actual passage (public KJV/WEB) plus a handful of
 * real cross-references, then invites the visitor to create a free account.
 */
const PublicVerseReader = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ref = params.get('ref') || 'John 3:16';
  const [translation, setTranslation] = useState('KJV');
  const [verse, setVerse] = useState(null);
  const [crossRefs, setCrossRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    const parsed = parseRef(ref);
    try {
      const v = await getVerse(ref, translation);
      setVerse(v);
    } catch (e) {
      setError(true);
    }
    if (parsed) {
      try {
        const r = await axios.get(`${API}/resources/tsk`, { params: parsed });
        setCrossRefs((r.data?.references || []).slice(0, 8));
      } catch (e) {
        setCrossRefs([]);
      }
    } else {
      setCrossRefs([]);
    }
    setLoading(false);
  }, [ref, translation]);

  useEffect(() => {
    load();
  }, [load]);

  const refLabel = (cr) =>
    `${cr.to_book} ${cr.to_chapter}:${cr.to_verse}${cr.to_verse_end ? '-' + cr.to_verse_end : ''}`;

  return (
    <div
      data-testid="public-verse-reader"
      className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white"
    >
      <header className="sticky top-0 z-10 border-b border-slate-800/80 bg-slate-950/70 backdrop-blur">
        <div className="container mx-auto max-w-4xl px-4 py-3 flex items-center justify-between">
          <Link to="/" data-testid="public-reader-home" className="flex items-center gap-1.5 text-amber-400 hover:text-amber-300">
            <ChevronLeft className="w-4 h-4" /> <span className="font-serif font-semibold">Lumera</span>
          </Link>
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
            <BookOpen className="w-3 h-3 mr-1" /> Free Preview
          </Badge>
        </div>
      </header>

      <main className="container mx-auto max-w-3xl px-4 py-12 sm:py-16">
        {loading ? (
          <div className="flex flex-col items-center py-24 text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin text-amber-400 mb-3" /> Loading Scripture…
          </div>
        ) : error || !verse ? (
          <div className="text-center py-24">
            <p className="text-slate-400">We couldn&apos;t load that passage.</p>
            <Button onClick={() => navigate('/')} className="mt-4 rounded-full">Back home</Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <p data-testid="public-reader-ref" className="text-amber-400 font-serif text-lg">
                {verse.book} {verse.chapter}:{verse.verse}
              </p>
              <div className="flex gap-1">
                {['KJV', 'WEB'].map((t) => (
                  <button
                    key={t}
                    data-testid={`public-reader-translation-${t.toLowerCase()}`}
                    onClick={() => setTranslation(t)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      translation === t ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-slate-300 hover:text-white'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <blockquote
              data-testid="public-reader-text"
              className="font-serif text-2xl sm:text-3xl leading-relaxed text-white border-l-2 border-amber-500/50 pl-5"
            >
              &ldquo;{verse.text}&rdquo;
            </blockquote>

            {crossRefs.length > 0 && (
              <div className="mt-10">
                <p className="flex items-center gap-2 text-sm text-slate-400 mb-3">
                  <Network className="w-4 h-4 text-amber-400" /> Cross-references
                </p>
                <div className="flex flex-wrap gap-2">
                  {crossRefs.map((cr, i) => (
                    <button
                      key={`${cr.to_book}-${cr.to_chapter}-${cr.to_verse}-${i}`}
                      data-testid={`public-xref-${i}`}
                      onClick={() => navigate(`/read?ref=${encodeURIComponent(refLabel(cr))}`)}
                      className="rounded-full bg-slate-800/70 px-3 py-1 text-sm text-slate-300 transition-colors hover:bg-amber-500/15 hover:text-amber-300"
                    >
                      {refLabel(cr)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-14 rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 to-purple-500/10 p-6 sm:p-8 text-center">
              <Sparkles className="w-7 h-7 text-amber-400 mx-auto mb-3" />
              <h2 className="font-serif text-2xl font-bold">Go deeper with the full study suite</h2>
              <p className="text-slate-400 mt-2 max-w-md mx-auto">
                Unlock AI insights, immersive audio, 19 translations, classic commentaries, Strong&apos;s word studies and
                the interactive 3D journeys — free.
              </p>
              <Button
                onClick={() => navigate('/login')}
                data-testid="public-reader-cta"
                className="mt-5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 rounded-full px-7 py-6 text-lg shadow-xl shadow-amber-500/30"
              >
                Create your free account <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default PublicVerseReader;
